import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Sparkles,
  Calendar,
  Layers,
  Leaf,
  Recycle,
  Tag,
  Boxes,
  PackageOpen,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Service de livraison de cartons à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Service de livraison de cartons à domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux et que charger des volumes
            de cartons plats dans votre voiture personnelle au milieu des
            grands axes de Saint-Quentin est une contrainte évitable,
            notre service de livraison s&apos;occupe de tout.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Dépose sur-mesure chez vous",
              body: "Nous acheminons l'intégralité de vos commandes directement au pied de votre immeuble (avec ascenseur ou escalier) ou sur le pas de votre porte à Montigny-le-Bretonneux, ainsi que dans les communes limitrophes comme Voisins-le-Bretonneux, Guyancourt, Trappes, Élancourt et Bois-d'Arcy.",
              Icon: Truck,
            },
            {
              title: "Ponctualité garantie",
              body: "Pas de journée bloquée à attendre le livreur. Nous fixons ensemble un créneau horaire précis et rigoureusement respecté selon votre emploi du temps pour vous permettre de démarrer vos cartons sans perdre une minute.",
              Icon: Calendar,
            },
            {
              title: "Fournitures prêtes à l'emploi",
              body: "Tous vos cartons vous sont remis à plat, dans un état de propreté irréprochable et totalement secs, préservant ainsi 100% de la rigidité de leur double cannelure industrielle.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Nos kits "Prêts à l'emploi" ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Kits clés en main
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Nos kits de déménagement « Prêts à l&apos;emploi » : la
              simplicité avant tout
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour vous éviter d&apos;oublier un accessoire indispensable
              au milieu de vos préparatifs, nous avons conçu des packs
              complets, pensés par des professionnels et prêts à être
              utilisés.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Le concept « Clés en main »",
                  body: "Plus besoin de calculer le nombre de cartons nécessaires. Nos kits regroupent tout le matériel indispensable (cartons, adhésifs, bulles) proportionnellement à la taille de votre logement.",
                  Icon: PackageOpen,
                },
                {
                  title: "Exemple — Notre Kit Appartement T3",
                  body: "C'est la configuration idéale pour les foyers intermédiaires. Ce pack contient un assortiment de cartons standards pour le linge, de cartons renforcés pour les livres et les objets lourds, ainsi que des rouleaux de film à bulles pour protéger la vaisselle et des adhésifs à forte adhérence.",
                  Icon: Boxes,
                },
                {
                  title: "Découvrez toutes nos configurations",
                  body: "Du studio étudiant à la grande maison familiale, nous proposons une gamme complète de kits adaptés à chaque projet. Rendez-vous sur notre boutique en ligne pour trouver le pack parfait pour votre déménagement.",
                  Icon: Sparkles,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/kits-cartons"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-montigny-le-bretonneux-cta-kits"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Découvrir tous nos kits
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <PackageOpen className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Pack T3 — le best-seller
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Le compromis parfait pour un appartement intermédiaire
              {" "}{city.aCity}. Tous les contenants nécessaires, livrés
              chez vous, prêts à l&apos;emploi.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons standards & renforcés
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Rouleaux de film à bulles
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Adhésifs à forte adhérence
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Quantités calculées par des pros
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Éco-Engagement ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Éco-engagement
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des cartons 100% recyclables et biodégradables
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Faire le choix de nos fournitures de déménagement{" "}
              {city.aCity}, c&apos;est aussi poser un geste concret pour
              l&apos;environnement. L&apos;intégralité de notre cartonnerie
              est fabriquée à partir de fibres de cellulose biosourcées,
              issues de forêts gérées durablement, et est 100% recyclable.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Une fois votre installation terminée dans votre nouveau
              logement, nos cartons se replient entièrement à plat en un
              clin d&apos;œil. Vous pouvez ainsi les stocker très
              facilement dans votre garage pour un usage ultérieur, les
              transmettre à des proches qui s&apos;apprêtent à déménager,
              ou les déposer dans les filières de tri local. Une démarche
              éco-responsable qui réduit drastiquement les déchets
              d&apos;emballage à usage unique.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Fibres biosourcées",
                body: "Issues de forêts gérées durablement, pour une empreinte carbone maîtrisée.",
                Icon: Leaf,
              },
              {
                title: "100% recyclable",
                body: "Vos cartons se replient à plat et rejoignent facilement les filières de tri local après usage.",
                Icon: Recycle,
              },
              {
                title: "Réemploi simplifié",
                body: "Stockables sans effort dans un garage, transmissibles à vos proches pour un second cycle de vie.",
                Icon: Layers,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Pourquoi choisir notre boutique ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir notre boutique pour vos déménagements ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire confiance à notre service de proximité, c&apos;est la
            garantie d&apos;une transition réussie grâce à quatre
            engagements majeurs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Robustesse professionnelle",
              body: "Nos emballages à double cannelure possèdent une excellente résistance à l'écrasement vertical, idéale pour empiler les cartons en toute sécurité dans le camion.",
              Icon: ShieldCheck,
            },
            {
              title: "Gain de temps absolu",
              body: "Vous passez commande en quelques clics depuis votre canapé et vous évitez la corvée des magasins de bricolage.",
              Icon: Sparkles,
            },
            {
              title: "Tarifs transparents",
              body: "Du carton à l'unité aux kits complets, nous proposons des tarifs compétitifs adaptés à tous les budgets, sans mauvaise surprise.",
              Icon: Tag,
            },
            {
              title: "Conseil de spécialistes",
              body: "Experts de la logistique en Île-de-France, nous vous guidons précisément sur les volumes à prévoir pour éviter la panne de carton le jour J.",
              Icon: CircleCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-montigny-le-bretonneux-cta-shop"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons & tarifs
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-white">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-montigny-le-bretonneux-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
